import { DeviceType } from '../schema';
import type { DeviceVariation } from '../../types/device';

/**
 * Service for transforming and processing wallpaper data
 */
export class WallpaperTransformService {
  /**
   * Generates search keywords from wallpaper metadata
   */
  generateSearchKeywords(
    title: string,
    description?: string,
    category?: string,
    tags?: string[]
  ): string[] {
    const keywords = new Set<string>();

    // Add title words
    if (title) {
      title.toLowerCase().split(/\s+/).forEach(word => {
        if (word.length > 2) keywords.add(word);
      });
    }

    // Add description words
    if (description) {
      description.toLowerCase().split(/\s+/).forEach(word => {
        if (word.length > 2) keywords.add(word);
      });
    }

    // Add category
    if (category) {
      keywords.add(category.toLowerCase());
    }

    // Add tags
    if (tags) {
      tags.forEach(tag => {
        if (tag.trim().length > 0) {
          keywords.add(tag.trim().toLowerCase());
        }
      });
    }

    return Array.from(keywords);
  }



  /**
   * Transforms device variations into legacy format for backward compatibility
   * NOTE: Legacy format only supports one variation per device, so we take the primary/first one
   * All variations are preserved in the enhanced format (deviceVariationsList)
   */
  transformVariationsToLegacyFormat(variations: DeviceVariation[]): Partial<Record<DeviceType, any>> {
    const legacyFormat: Partial<Record<DeviceType, unknown>> = {};

    // Group variations by device type and take the primary (first valid) one for legacy compatibility
    const deviceGroups = new Map<DeviceType, DeviceVariation[]>();

    variations.forEach(variation => {
      if (!deviceGroups.has(variation.device)) {
        deviceGroups.set(variation.device, []);
      }
      deviceGroups.get(variation.device)!.push(variation);
    });

    // Convert to legacy format - only store the primary variation per device
    deviceGroups.forEach((deviceVariations, device) => {
      // Find the primary variation (marked as primary) or take the first valid one
      const primaryVariation = deviceVariations.find(v => v.isPrimary && v.isValid && v.processedUrls) ||
        deviceVariations.find(v => v.isValid && v.processedUrls) ||
        deviceVariations[0]; // Fallback to first variation

      if (primaryVariation && primaryVariation.processedUrls) {
        legacyFormat[device] = {
          imageUrl: primaryVariation.imageUrl,
          thumbnailUrl: primaryVariation.processedUrls.thumbnailUrl,
          resolutions: primaryVariation.processedUrls.resolutions?.map((res) => ({
            width: res.width,
            height: res.height,
            url: res.url,
            size: res.size
          })) || [],
          dimensions: {
            width: primaryVariation.imageInfo?.width || 0,
            height: primaryVariation.imageInfo?.height || 0
          },
          validationScore: primaryVariation.imageInfo?.qualityScore
        };
      }
    });

    return legacyFormat;
  }

  /**
   * Transforms device variations into enhanced format supporting multiple variations per device
   */
  transformVariationsToEnhancedFormat(variations: DeviceVariation[]): Array<{
    device: DeviceType;
    variations: Array<{
      id: string;
      imageUrl: string;
      thumbnailUrl?: string;
      resolutions?: Array<{
        width: number;
        height: number;
        url: string;
        size: string;
      }>;
      dimensions: {
        width: number;
        height: number;
      };
      validationScore?: number;
      isPrimary: boolean;
    }>;
  }> {
    const enhancedFormat: Array<{
      device: DeviceType;
      variations: Array<{
        id: string;
        imageUrl: string;
        thumbnailUrl?: string;
        resolutions?: Array<{
          width: number;
          height: number;
          url: string;
          size: string;
        }>;
        dimensions: {
          width: number;
          height: number;
        };
        validationScore?: number;
        isPrimary: boolean;
      }>;
    }> = [];

    // Group variations by device type
    const deviceGroups = new Map<DeviceType, DeviceVariation[]>();

    variations.forEach(variation => {
      if (!deviceGroups.has(variation.device)) {
        deviceGroups.set(variation.device, []);
      }
      deviceGroups.get(variation.device)!.push(variation);
    });

    // Transform each device group
    deviceGroups.forEach((deviceVariations, device) => {
      const validVariations = deviceVariations.filter(v => v.isValid && v.processedUrls);

      if (validVariations.length > 0) {
        enhancedFormat.push({
          device,
          variations: validVariations.map((variation, index) => ({
            id: `${device}-${index}`,
            imageUrl: variation.imageUrl,
            thumbnailUrl: variation.processedUrls?.thumbnailUrl,
            resolutions: variation.processedUrls?.resolutions?.map((res) => ({
              width: res.width,
              height: res.height,
              url: res.url,
              size: res.size
            })),
            dimensions: {
              width: variation.imageInfo?.width || 0,
              height: variation.imageInfo?.height || 0
            },
            validationScore: variation.imageInfo?.qualityScore,
            isPrimary: variation.isPrimary || index === 0 // Use stored isPrimary or default to first variation
          }))
        });
      }
    });

    return enhancedFormat;
  }
}

export const wallpaperTransformService = new WallpaperTransformService();